/* eslint-disable react-hooks/exhaustive-deps */
import { useLocation } from 'react-router'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../button/button'
import { Icon } from '../icon/icon'
import Text from '../text/text'
import { SearchSheet } from './header-search-sheet'
import {
	getCountryCodeByLanguage,
	getCountryName,
	useClientLang,
} from '~/utils/i18n.utils'
import {
	isMapRoute,
	isPlaceRoute,
	isRootRoute,
} from '~/utils/location/location.route.helpers'
import { useUserCoordinates, useUserLocation } from '~/utils/location/location'
import { capitalizeText, cn } from '~/utils/misc'
import { useUserQueries } from '~/utils/queries/userUrlQueries'

export default function HeaderSearch() {
	const location = useLocation()
	const [showSheet, setShowSheet] = useState<boolean>(false)
	const [selectedSection, setSelectedSection] = useState<number>(0)

	const locationData = useUserLocation()
	const userLocation = locationData?.userLocation

	const queries = useUserQueries()
	const userCoordinates = useUserCoordinates()

	const { t } = useTranslation()
	const lang = useClientLang().lngShort

	const isMapPage = isMapRoute(location.pathname)
	const isRootPage = isRootRoute(location.pathname)
	const isPlacePage = isPlaceRoute(location.pathname)
	const showSearch = isRootPage || isMapPage || isPlacePage

	const codeByLanguage = getCountryCodeByLanguage(lang)

	const locationQuery =
		userLocation?.city ??
		(userLocation?.country
			? getCountryName(userLocation?.country, lang)
			: undefined) ??
		(isMapPage
			? t('public_map_area')
			: !isPlacePage
				? getCountryName(codeByLanguage, lang)
				: undefined)

	const getDatesText = useMemo(() => {
		let text = t('misc_all_dates')
		if (queries.fromDate) {
			text = new Intl.DateTimeFormat(lang, {
				day: '2-digit',
				month: 'short',
			}).format(queries.fromDate)
		}
		if (
			queries.fromDate &&
			queries.toDate &&
			queries.fromDate.getTime() !== queries.toDate.getTime()
		) {
			text +=
				' - ' +
				new Intl.DateTimeFormat(lang, {
					day: '2-digit',
					month: 'short',
				}).format(queries.toDate)
		}
		return text
	}, [queries.fromDate, queries.toDate])

	const getLocationText = useMemo(() => {
		let text = t('public_everywhere')
		if (locationQuery) {
			text = locationQuery
		} else if (
			userLocation?.swLat &&
			userLocation?.swLng &&
			userLocation?.neLat &&
			userLocation?.neLng
		) {
			text = t('public_map_area')
		} else if (
			userCoordinates?.latitude &&
			userCoordinates?.longitude &&
			userLocation?.nearbySelected
		) {
			text = t('public_near_you')
		}
		return capitalizeText(text)
	}, [locationQuery, userLocation, userCoordinates])

	const getSearchText = useMemo(() => {
		let text = t('action_search')
		if (queries.searchText) {
			text = queries.searchText
		}
		return text
	}, [queries.searchText])

	const openSearchSheet = (section: number) => {
		setSelectedSection(section)
		setShowSheet(true)
	}

	const baseSearchClass =
		'relative mx-0 flex h-12 w-full cursor-pointer items-center rounded-full border border-foreground/20 bg-card pr-10 text-center text-body-sm shadow-md duration-200 hover:shadow-lg md:h-10 lg:w-[500px]'

	return (
		<>
			{showSearch ? (
				<>
					<div className={cn(baseSearchClass, 'justify-evenly md:w-[450px]')}>
						<button
							type="button"
							className="hover:bg-secondary/30 ml-0.5 w-4/12 cursor-pointer truncate rounded-full p-2 duration-100"
							onClick={() => openSearchSheet(0)}
						>
							{getSearchText}
						</button>
						<button
							type="button"
							className="hover:bg-secondary/30 w-4/12 cursor-pointer truncate rounded-full p-2 duration-100"
							onClick={() => openSearchSheet(1)}
						>
							{getLocationText}
						</button>
						<button
							type="button"
							className="hover:bg-secondary/30 w-4/12 cursor-pointer truncate rounded-full p-2 px-1 duration-100"
							onClick={() => openSearchSheet(2)}
						>
							{getDatesText}
						</button>
						<div className="absolute top-[3px] right-[4px]">
							<Button
								variant="secondary"
								size="sm"
								className="h-10! w-10! rounded-full! p-3! md:h-8! md:w-8! md:p-2!"
								title={t('action_search_and_filter')}
								type="button"
								onClick={() => openSearchSheet(0)}
							>
								<Icon name="search" size="lg" />
							</Button>
						</div>
					</div>
				</>
			) : (
				<>
					<button
						className={cn(
							baseSearchClass,
							'hover:bg-secondary/20 justify-start pl-6 duration-100 md:w-[300px] lg:w-[350px]',
						)}
						onClick={() => openSearchSheet(0)}
					>
						<Text as="sm">{t('action_start_new_search')}</Text>
						<div className="absolute top-[3px] right-[4px]">
							<div className="bg-secondary h-10! w-10! rounded-full! p-3! text-xs md:h-8! md:w-8! md:p-2!">
								<Icon name="search" size="sm" className="text-white" />
							</div>
						</div>
					</button>
				</>
			)}

			<SearchSheet
				isOpen={showSheet}
				onOpenChange={setShowSheet}
				selectedSection={selectedSection}
				trigger={<div />}
			/>
		</>
	)
}
